import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  LinkedNamedEntityForEpisodeItem,
  LinkedNamedEntityReference,
} from '@insights_module/types/ContextualAndEntity';
import { NAMED_ENTITY_TYPE } from '@insights_module/constants/contextualAndEntity';
import { useSelectedEpisodeDetails } from '@insights_module/hooks/useGraphqlAudioDetails';
import { fetchAudioLinkedNamedEntities } from '@services/index';

type LinkedNamedEntityReferenceAccType = LinkedNamedEntityReference & {
  timestamp?: [number, number];
};

function groupEntityReferencesByValue(arr: LinkedNamedEntityReference[]) {
  return arr.reduce((acc, reference) => {
    // Find if there's already an entry with the same value
    const existing: LinkedNamedEntityReference | undefined = acc.find(
      (item: LinkedNamedEntityReference) => item.value === reference.value
    );

    if (existing && existing?.timestamps) {
      // If an entry with the same value exists, push the timestamp to timestamps array
      existing?.timestamps.push(reference.timestamp as [number, number]);
    } else {
      // If no entry exists, add a new entry
      acc.push({
        value: reference.value,
        timestamps: [reference.timestamp as [number, number]],
      });
    }

    return acc;
  }, [] as LinkedNamedEntityReferenceAccType[]);
}

export const useEpisodeLinkedNamedEntities = () => {
  const { data: selectedEpisodeDetails, isLoading: isEpisodeDetailsLoading } =
    useSelectedEpisodeDetails();
  const { data, isLoading } = useQuery({
    queryKey: ['audio-linked-named-entities', { id: selectedEpisodeDetails?.id }],
    queryFn: () => fetchAudioLinkedNamedEntities(selectedEpisodeDetails?.id),
    staleTime: 60 * 60 * 1000, // 1 hour
    enabled: !!selectedEpisodeDetails && !isEpisodeDetailsLoading,
  });

  const linkedNamedEntities = useMemo(() => {
    if (isEpisodeDetailsLoading || (!isLoading && !data?.data?.linked_named_entities?.length))
      return [];

    if (data?.data?.linked_named_entities?.length) {
      return data?.data?.linked_named_entities
        .filter((entity: LinkedNamedEntityForEpisodeItem) =>
          Object.values(NAMED_ENTITY_TYPE).includes(entity.type)
        )
        .map((item: LinkedNamedEntityForEpisodeItem) => {
          const references = groupEntityReferencesByValue(item.references).map(
            (ref: LinkedNamedEntityReference) => ({
              ...ref,
              count: ref?.timestamps?.length,
            })
          );

          return {
            ...item,
            references,
            referencesCount: references.reduce((acc, current) => (acc += current?.count || 0), 0),
          };
        })
        .sort(
          // Sort entities by number of references. From highest to lowest number.
          (a: LinkedNamedEntityForEpisodeItem, b: LinkedNamedEntityForEpisodeItem) =>
            b.referencesCount - a.referencesCount
        );
    }
  }, [data, isEpisodeDetailsLoading, isLoading]) as LinkedNamedEntityForEpisodeItem[];

  return {
    linkedNamedEntities,
    isLoading,
  };
};
